import { DEFAULT_PROGRESS_BAR } from "@shopflo/ui/progress-bar";
import { getTextColorByBrightness } from "@shopflo/ui/utils";
import { CartFooter } from "components/cart/CartFooter";
import { CartHeader } from "components/cart/CartHeader";
import { ItemCard } from "components/checkout/ItemCard";
import CartBannerStrip from "components/common/notification-strips/CartBannerStrip";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useToken } from "lib/contexts/TokenProvider";
import { BannerType, ItemCardProps } from "lib/types/checkout";
import { getProgressBarConfig } from "lib/utils/checkout";
import { getCardBorderStyles } from "lib/utils/UIUtils";
import { useMemo } from "react";

// Getter function to provide ItemCard props
const getItemCardProps = (item: Record<string, any>): ItemCardProps => ({
  item_id: item.id,
  item_title: item.product_title,
  quantity: item.quantity,
  price: String(item.final_price / 100),
  image: item.image,
  disableOOSItems: false,
  checkoutItemsMutable: true,
  isCartItem: true,
  isUpdating: true,
  setIsUpdating: () => { },
  itemLevelAddons: [],
  is_freebie: false,
  variant_title: item.variant_title,
  is_available: true,
  originalPrice: String(item.price / 100),
  markupPrice: String(item.price / 100),
  availableQuantity: item.quantity,
  isMutable: true,
  variant_id: item.variant_id,
  product_id: item.product_id,
  item_url: item.url,
  is_discount_alteration: false,
  is_platform_fee: false,
  hasProductSelector: false,
  appliedItemAddOns: [],
  rawItem: {},
  components: [],
  has_components: item.has_components,
  is_upsell: item.is_upsell,
});

export const DummyCart = () => {
  const {
    state: { cartJSData },
  } = useToken();

  const {
    state: {
      cartMetadata: { uiAttributes },
    },
  } = useMerchantContext();
  const progressBarConfig = uiAttributes?.progressBarAttributes
    ? getProgressBarConfig(uiAttributes?.progressBarAttributes)
    : DEFAULT_PROGRESS_BAR;

  const topBannerConfig = useMemo(() => {
    return uiAttributes?.bannerConfig?.find((banner) => banner.type === "CART");
  }, [uiAttributes]);

  const bottomBannerConfig = useMemo(() => {
    return uiAttributes?.bottomBannerConfig?.find((banner) => banner.type === "CART");
  }, [uiAttributes]);

  return (
    <div className="items-between flex h-full flex-col bg-white relative">
      <CartHeader totalItems={cartJSData?.item_count} customisations={uiAttributes?.header} showBottomShadow={!topBannerConfig?.enabled} />
      {topBannerConfig?.enabled && <CartBannerStrip bannerPosition={BannerType.HEADER} bannerConfig={topBannerConfig} />}
      <div className="flex flex-1 flex-col gap-3 overflow-auto overflow-x-hidden p-3 pb-5"
        style={{
          backgroundColor: uiAttributes?.page?.backgroundColor,
        }}
      >
        {progressBarConfig?.isEnabled && <ProgressBarSkeleton />}
        <div className="bg-zinc-100 rounded-xl p-[10px]"
          style={{
            backgroundColor: uiAttributes?.orderSummaryConfig?.backgroundColor,
            borderRadius: uiAttributes?.orderSummaryConfig?.cardRadius,
            ...getCardBorderStyles({
              borderSides: uiAttributes?.orderSummaryConfig?.cardBorderSides,
              borderWidth: uiAttributes?.orderSummaryConfig?.cardBorderWidth,
              borderColor: uiAttributes?.orderSummaryConfig?.borderColor,
            }),
          }}
        >
          <DummyCartItems />
        </div>
      </div>
      <CartFooter setDialog={() => { }} isLoading={true} primaryCTAText={"Checkout"} bottomBannerConfig={bottomBannerConfig} />
    </div>
  );
};

const DummyCartItems = () => {
  const {
    state: { cartJSData },
  } = useToken();

  const {
    state: {
      cartMetadata: { uiAttributes },
    },
  } = useMerchantContext();

  return (
    <ul className="flex w-full flex-col gap-5 [&>li]:relative [&>li:not(:last-child)]:after:content-[''] [&>li:not(:last-child)]:after:absolute [&>li:not(:last-child)]:after:left-0 [&>li:not(:last-child)]:after:right-0 [&>li:not(:last-child)]:after:bottom-[-10px] [&>li:not(:last-child)]:after:h-[0.5px] [&>li:not(:last-child)]:after:bg-[var(--divider-color)]"
      style={{
        ['--divider-color' as any]: getTextColorByBrightness({ bgHexColor: uiAttributes?.orderSummaryConfig?.backgroundColor ?? '#FFFFFF', lightOpacity: 24 }),
      }}>
      {cartJSData?.items?.map((item: ItemCardProps, index: number) => {
        return (
          <li key={`${item.item_id}_${index}`}>
            <ItemCard {...getItemCardProps(item)} customisations={uiAttributes?.orderSummaryConfig} />
          </li>
        );
      })}
    </ul>
  );
};

const ProgressBarSkeleton = () => {
  return (
    <div className="flex flex-col justify-between w-full h-[100px] bg-gradient-to-b from-transparent via-gray-200/10 to-gray-200/20 rounded-lg px-3 py-8">
      {/* Top progress bar skeleton */}
      <div className="flex justify-center">
        <div className="w-1/3 h-[10px] bg-gray-400/10 rounded-full animate-pulse" />
      </div>

      {/* Bottom progress bar skeleton */}
      <div className="w-full h-[10px] bg-gray-400/10 rounded-full animate-pulse"></div>
    </div>
  );
};
